import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Facebook, Instagram, Twitter, Youtube, Linkedin, Globe } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useToast } from '../../hooks/use-toast';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useGeneralSettings } from '../../contexts/GeneralSettingsContext';
import { useCategories } from '../../contexts/CategoriesContext';
import CountrySelector from '../CountrySelector';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const Footer = () => {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const { countryCode } = useLocalization();
  const { settings: generalSettings } = useGeneralSettings();
  const { getParentCategories } = useCategories();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [helpPages, setHelpPages] = useState([]);
  const [legalPages, setLegalPages] = useState([]);

  const categories = getParentCategories().slice(0, 8); // Top 8 categories

  useEffect(() => {
    if (countryCode) {
      fetchHelpPages();
      fetchLegalPages();
      // Social networks now come from generalSettings context
    }
  }, [countryCode]);

  const fetchHelpPages = async () => {
    try {
      // "Ayuda" shows the informational pages (Quiénes Somos, FAQs, Envíos
      // y Devoluciones) — distinct from the bottom bar's legal links.
      const response = await axios.get(`${API}/pages`, {
        params: {
          country: countryCode,
          category: 'information'
        }
      });
      setHelpPages(response.data || []);
    } catch (error) {
      console.error('Error fetching help pages:', error);
    }
  };

  const fetchLegalPages = async () => {
    try {
      // Fetch pages in Legal category
      const response = await axios.get(`${API}/pages`, {
        params: { 
          country: countryCode,
          category: 'legal' // Fetch pages from 'legal' category
        }
      });
      setLegalPages(response.data || []);
    } catch (error) {
      console.error('Error fetching legal pages:', error);
    }
  };

  const getIconComponent = (iconName) => {
    const icons = {
      Facebook, Instagram, Twitter, Youtube, Linkedin, Globe
    };
    const Icon = icons[iconName] || Globe;
    return Icon;
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/contact/newsletter/subscribe`, {
        email,
        name: ''
      });
      
      toast({
        title: t('common.success'),
        description: response.data.message,
      });
      setEmail('');
    } catch (error) {
      toast({
        title: t('common.error'),
        description: t('newsletterForm.error'),
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="mt-20" style={{ background: 'var(--bg-dark)', color: '#d8d0c0' }}>
      <div className="container mx-auto px-6 md:px-10 pt-24 pb-10">
        {/* Top section — large editorial logo + tagline */}
        <div className="text-center mb-16">
          <Link to="/" className="inline-block mb-4">
            <img
              src="/logo-cimonamia.png"
              alt="Cimonamia Logo"
              className="h-12 md:h-14 w-auto mx-auto"
              style={{ filter: 'brightness(0) invert(1)' }}
            />
          </Link>
          <p
            className="italic mt-2"
            style={{
              fontFamily: 'var(--font-display)',
              fontVariationSettings: "'opsz' 144, 'SOFT' 100",
              fontSize: '16px',
              color: '#d8d0c0',
              letterSpacing: '0.5px',
            }}
          >
            {t('hero.tagline')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          {/* Brand + social */}
          <div>
            <h3
              className="mb-5"
              style={{
                fontFamily: 'var(--font-body)',
                fontSize: '11px',
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '3px',
                color: '#f5f0e8',
              }}
            >
              {t('footer.followUs')}
            </h3>
            <div className="flex gap-3">
              {generalSettings?.socialNetworks?.length > 0 ? (
                generalSettings.socialNetworks.map((social) => {
                  const Icon = getIconComponent(social.icon);
                  return (
                    <a
                      key={social.id}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="h-9 w-9 rounded-full flex items-center justify-center transition-colors"
                      style={{ background: 'rgba(255,255,255,0.08)' }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--accent)')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.08)')}
                      aria-label={social.name}
                    >
                      <Icon className="h-4 w-4" />
                    </a>
                  );
                })
              ) : (
                <>
                  {[Facebook, Instagram, Twitter, Youtube].map((Icon, i) => (
                    <a
                      key={i}
                      href="#"
                      className="h-9 w-9 rounded-full flex items-center justify-center transition-colors"
                      style={{ background: 'rgba(255,255,255,0.08)' }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--accent)')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.08)')}
                      aria-label="Social"
                    >
                      <Icon className="h-4 w-4" />
                    </a>
                  ))}
                </>
              )}
            </div>
          </div>

          {/* Shop */}
          <div>
            <h3
              className="mb-5"
              style={{
                fontFamily: 'var(--font-body)',
                fontSize: '11px',
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '3px',
                color: '#f5f0e8',
              }}
            >
              {t('footer.shop')}
            </h3>
            <ul className="space-y-3">
              {categories.map((category) => {
                let displayName = category.slug;
                if (category.versions && category.versions.length > 0) {
                  const firstVersion = category.versions.find((v) => v.name) || category.versions[0];
                  displayName = firstVersion?.name || category.slug;
                } else if (category.name) {
                  const currentLang = i18n.language || 'es';
                  displayName = category.name?.[currentLang] || category.name?.es || category.name;
                }
                return (
                  <li key={category.slug}>
                    <Link
                      to={`/shop/${category.slug}`}
                      className="transition-colors"
                      style={{ fontSize: '13px', color: '#a8a095' }}
                      onMouseEnter={(e) => (e.currentTarget.style.color = '#f5f0e8')}
                      onMouseLeave={(e) => (e.currentTarget.style.color = '#a8a095')}
                    >
                      {displayName}
                    </Link>
                  </li>
                );
              })}
              <li>
                <Link
                  to="/shop"
                  className="transition-colors"
                  style={{ fontSize: '13px', color: '#a8a095' }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = '#f5f0e8')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = '#a8a095')}
                >
                  {t('categories.allCategories')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Help */}
          <div>
            <h3
              className="mb-5"
              style={{
                fontFamily: 'var(--font-body)',
                fontSize: '11px',
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '3px',
                color: '#f5f0e8',
              }}
            >
              {t('footer.help')}
            </h3>
            {helpPages.length > 0 ? (
              <ul className="space-y-3">
                {helpPages.map((page) => {
                  const isAboutUs = page.slug === 'about-us' || page.slug === 'quienes-somos' || page.slug === 'quienes_somos';
                  return (
                    <React.Fragment key={page.slug}>
                      <li>
                        <Link
                          to={`/page/${page.slug}`}
                          className="transition-colors"
                          style={{ fontSize: '13px', color: '#a8a095' }}
                          onMouseEnter={(e) => (e.currentTarget.style.color = '#f5f0e8')}
                          onMouseLeave={(e) => (e.currentTarget.style.color = '#a8a095')}
                        >
                          {page.title}
                        </Link>
                      </li>
                      {/* ReBorn explainer — placed directly under About Us regardless
                          of the fetched pages' order, since it's a dedicated route
                          (/reborn), not a generic `pages` CMS record. */}
                      {isAboutUs && (
                        <li>
                          <Link
                            to="/reborn"
                            className="transition-colors"
                            style={{ fontSize: '13px', color: '#a8a095' }}
                            onMouseEnter={(e) => (e.currentTarget.style.color = '#f5f0e8')}
                            onMouseLeave={(e) => (e.currentTarget.style.color = '#a8a095')}
                          >
                            ReBorn
                          </Link>
                        </li>
                      )}
                    </React.Fragment>
                  );
                })}
              </ul>
            ) : (
              <p style={{ fontSize: '13px', color: '#7a7268' }}>—</p>
            )}
          </div>

          {/* Newsletter */}
          <div>
            <h3
              className="mb-5"
              style={{
                fontFamily: 'var(--font-body)',
                fontSize: '11px',
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '3px',
                color: '#f5f0e8',
              }}
            >
              {t('footer.newsletter')}
            </h3>
            <p style={{ fontSize: '13px', color: '#a8a095', marginBottom: '20px', lineHeight: 1.6 }}>
              {t('footer.newsletterText')}
            </p>
            <form onSubmit={handleSubscribe} className="flex flex-col gap-3">
              <input
                type="email"
                placeholder={t('footer.enterEmail')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                style={{
                  background: 'transparent',
                  border: 'none',
                  borderBottom: '1px solid rgba(216,208,192,0.3)',
                  color: '#f5f0e8',
                  padding: '10px 0',
                  fontSize: '14px',
                  outline: 'none',
                  fontFamily: 'var(--font-body)',
                }}
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-outline"
                style={{
                  color: '#f5f0e8',
                  borderColor: '#f5f0e8',
                  alignSelf: 'flex-start',
                  marginTop: '8px',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f5f0e8';
                  e.currentTarget.style.color = 'var(--ink)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = '#f5f0e8';
                }}
              >
                {t('footer.subscribe')}
              </button>
            </form>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex flex-col md:flex-row items-center gap-4">
              <p style={{ fontSize: '12px', color: '#a8a095', letterSpacing: '0.3px' }}>
                {t('footer.copyright')}
              </p>
              <div className="scale-95">
                <CountrySelector />
              </div>
            </div>
            <div className="flex flex-wrap justify-center gap-6">
              {legalPages.length > 0 ? (
                legalPages.map((page) => (
                  <Link
                    key={page.slug}
                    to={`/page/${page.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="transition-colors"
                    style={{ fontSize: '12px', color: '#a8a095' }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = '#f5f0e8')}
                    onMouseLeave={(e) => (e.currentTarget.style.color = '#a8a095')}
                  >
                    {page.title}
                  </Link>
                ))
              ) : (
                <>
                  <Link to="/privacy" className="transition-colors" style={{ fontSize: '12px', color: '#a8a095' }}>
                    Privacy Policy
                  </Link>
                  <Link to="/terms" className="transition-colors" style={{ fontSize: '12px', color: '#a8a095' }}>
                    Terms of Service
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;